import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { trackAddToCart } from "@/lib/analytics";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "kc_cart_v1";

const lineKey = (slug, size, color) => `${slug}__${size || "one"}__${color || "one"}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, size, qty = 1, color = null) => {
    const key = lineKey(product.slug, size, color);
    const match = (v, requireAvail) =>
      (!size || v.size === size) && (!color || v.color === color) && (!requireAvail || v.available);
    const variantId =
      product.variants?.find((v) => match(v, true))?.variant_id ||
      product.variants?.find((v) => match(v, false))?.variant_id ||
      product.variants?.find((v) => v.available)?.variant_id ||
      product.default_variant_id ||
      null;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        {
          key,
          slug: product.slug,
          title: product.title,
          price: product.price,
          image: product.images?.[0],
          size: size || null,
          color: color || null,
          variant_id: variantId,
          qty,
        },
      ];
    });
    trackAddToCart(product, qty);
    setOpen(true);
  };

  const updateQty = (key, qty) => {
    if (qty <= 0) return removeItem(key);
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty } : i)));
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clear = () => setItems([]);
  const updateNote = (key, note) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, note } : i)));

  // Alteration line — rides to checkout as its own Shopify variant charge.
  // variantId comes from the hidden "Alteration Service" product (price-matched).
  const addAlteration = (product, option, variantId, size = null, color = null) => {
    const key = `alt__${product.slug}__${option.id}`;
    setItems((prev) => {
      if (prev.some((i) => i.key === key)) return prev;
      return [
        ...prev,
        {
          key,
          slug: "alteration-service",
          title: `Alteration — ${option.label}`,
          forProduct: product.title,
          price: option.price,
          image: null,
          size: null,
          color: null,
          variant_id: variantId,
          qty: 1,
          isAlteration: true,
        },
      ];
    });
  };

  const value = useMemo(() => {
    const count = items.reduce((s, i) => s + i.qty, 0);
    const rawSubtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
    return {
      items,
      count,
      rawSubtotal,
      open,
      setOpen,
      addItem,
      addAlteration,
      updateQty,
      removeItem,
      updateNote,
      clear,
    };
  }, [items, open]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
